
import React from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";

import LoadingWrapper from "components/Shared/LoadingWrapper";
import RichTextEditor from "components/Shared/RichTextEditor";
import FileDropzone from "components/Shared/FileDropzone";

import { useListListQuery } from "store/services/listService";
import { ENTITY_LIST_CONSTANTS } from "data/static/entityListOptions";
import { useDOUploadFile } from "hooks";
import { toast } from "react-toastify";

const BlogForm = ({
  blogDetail = null,
  isBusy = false,
  onSubmit = () => { },
}) => {
  const navigate = useNavigate();
  const { handleUploadFiles } = useDOUploadFile();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      image: blogDetail?.image ? [blogDetail.image] : [],
      featureImage: blogDetail?.featureImage ? [blogDetail.featureImage] : [],
      title: blogDetail?.title ?? "",
      author: blogDetail?.author ?? "",
      content: blogDetail?.content ?? "",
      category: blogDetail?.category?._id ?? "",
      tags: blogDetail?.tags ?? "",
      slug: blogDetail?.slug ?? "",
      isFeatured: blogDetail?.isFeatured ?? false,
      isActive: blogDetail?.isActive ?? true,
    },
  });

  const { data: blogCategories = [], isLoading: isLoadingCategories } =
    useListListQuery({
      type: ENTITY_LIST_CONSTANTS.BLOG_CATEGORIES,
      isSubType: false,
      includeChildren: true,
      status: "Active",
    });

  React.useEffect(() => {
    if (!blogDetail) return;

    reset({
      ...blogDetail,
      category: blogDetail?.category?._id ?? "",
      image: blogDetail?.image ? [{ url: blogDetail.image }] : [],
      authorImage: blogDetail?.authorImage ? [{ url: blogDetail.authorImage }] : [],
    });
  }, [blogDetail, reset]);

  const prepareUploads = async (files, folder) => {
    const [existing, fresh] = files.reduce(
      ([existing, fresh], file) =>
        file?.publicId || file?.url
          ? [[...existing, file], fresh]
          : [existing, [...fresh, file]],
      [[], []]
    );

    let uploaded = [];
    if (fresh.length) {
      const { uploadedFiles } = await handleUploadFiles(fresh, {
        folderName: folder,
        entity: "blog",
      });
      uploaded = uploadedFiles;
    }

    return [...existing, ...uploaded][0] ?? null;
  };

  return (
    // Inside BlogForm component
    <LoadingWrapper loading={isBusy || isLoadingCategories}>
      <Box padding={3}>
        <form onSubmit={handleSubmit(async (data) => {
          try {
            const finalImage = await prepareUploads(data.image, "BLOG_IMAGE");
            const finalFeatureImage = await prepareUploads(data.featureImage || [], "BLOG_FEATURE_IMAGE");
            const payload = {
              ...data,
              image: finalImage,
              featureImage: finalFeatureImage,
            };
            onSubmit(payload);
          } catch (error) {
            console.error("Error uploading blog images:", error);
            toast.error("Image upload failed. Please try again.");
          }
        })}>
          <Grid container spacing={4}>
            {/* Main Content Area */}
            <Grid item xs={12} md={8}>
              {/* Title */}
              <Controller
                name="title"
                control={control}
                rules={{ required: "Title is required" }}
                render={({ field }) => (
                  <TextField
                    fullWidth
                    label="Enter title here"
                    variant="outlined"
                    size="medium"
                    {...field}
                    sx={{ mb: 3 }}
                  />
                )}
              />
              {errors.title && <p className="error">{errors.title.message}</p>}

              {/* Content */}
              <Controller
                name="content"
                control={control}
                rules={{ required: "Content is required" }}
                render={({ field: { onChange } }) => (
                  <Box sx={{ minHeight: 600 }}>
                    <RichTextEditor
                      height={500}
                      initialValue={
                        blogDetail?.content
                          ? blogDetail.content
                          : "<p>Enter Blog Content.</p>"
                      }
                      onChange={onChange}
                      error={!!errors?.content}
                      helperText={errors?.content?.message}
                    />
                  </Box>
                )}
              />

            </Grid>

            {/* Sidebar */}
            <Grid item xs={12} md={4}>
              <Grid container spacing={2}>
                {/* Profile Image */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Profile Image
                  </Typography>
                  <Controller
                    name="image"
                    control={control}
                    rules={{ required: "Profile image is required" }}
                    render={({ field }) => (
                      <FileDropzone
                        multiple={false}
                        maxFiles={1}
                        files={field?.value ?? []}
                        onFileChange={(files) => field.onChange(files)}
                        dropzoneStyles={{
                          width: "100%",
                          height: 180,
                          objectFit: "cover",
                        }}
                      />
                    )}
                  />
                  {errors.image && <p className="error">{errors.image.message}</p>}
                </Grid>

                {/* Author */}
                <Grid item xs={12}>
                  <Controller
                    name="author"
                    control={control}
                    rules={{ required: "Author is required" }}
                    render={({ field }) => (
                      <TextField fullWidth label="Author" size="small" {...field} />
                    )}
                  />
                  {errors.author && <p className="error">{errors.author.message}</p>}
                </Grid>

                {/* Category */}
                <Grid item xs={12}>
                  <Controller
                    name="category"
                    control={control}
                    rules={{ required: "Select a category" }}
                    render={({ field }) => (
                      <FormControl fullWidth size="small">
                        <InputLabel id="select-category">Category</InputLabel>
                        <Select
                          labelId="select-category"
                          label="Category"
                          {...field}
                        >
                          {blogCategories?.map((cat) => (
                            <MenuItem key={cat._id} value={cat._id}>
                              {cat.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                  {errors.category && (
                    <p className="error">{errors.category.message}</p>
                  )}
                </Grid>

                {/* Tags */}
                <Grid item xs={12}>
                  <Controller
                    name="tags"
                    control={control}
                    rules={{ required: "Tag is required" }}
                    render={({ field }) => (
                      <TextField
                        fullWidth
                        label="Tags (comma separated)"
                        size="small"
                        {...field}
                      />
                    )}
                  />
                  {errors.tags && <p className="error">{errors.tags.message}</p>}
                </Grid>

                {/* Feature Image */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Feature Image
                  </Typography>
                  <Controller
                    name="featureImage"
                    control={control}
                    render={({ field }) => (
                      <FileDropzone
                        multiple={false}
                        maxFiles={1}
                        label="Upload Feature Image"
                        files={field?.value ?? []}
                        onFileChange={(files) => field.onChange(files)}
                        dropzoneStyles={{
                          width: "100%",
                          height: 180,
                          objectFit: "cover",
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* Toggles */}
                <Grid item xs={12}>
                  <Controller
                    name="isActive"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Switch
                            name="isActive"
                            color="primary"
                            checked={field.value}
                            {...field}
                          />
                        }
                        label="Is Active?"
                      />
                    )}
                  />
                  <Controller
                    name="isFeatured"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Switch
                            name="isFeatured"
                            color="primary"
                            checked={field.value}
                            {...field}
                          />
                        }
                        label="Is Featured?"
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {/* Footer Buttons */}
          <Divider sx={{ my: 3 }} />
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button disabled={isBusy} onClick={() => navigate(-1)}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={isBusy}>
              {blogDetail ? "Update" : "Add"}
            </Button>
          </Box>
        </form>
      </Box>
    </LoadingWrapper>


  );
};

export default BlogForm;
